<script setup lang="ts">
  // Vue core imports
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  // External library imports
  import { BellRinging04Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPSkeleton from '@ui/components/CPSkeleton.vue'
  import CPText from '@ui/components/CPText.vue'

  // Internal imports
  import NotificationItem from './NotificationItem.vue'

  import { injectNotificationItemActions } from '../composables/useNotificationItemActions'

  import type { INotification } from '../types/notification.types'

  // Props definitions
  const props = defineProps({
    notifications: {
      type: Array as () => INotification[],
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    isFetchingNextPage: {
      type: Boolean,
      required: true,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'itemClicked', notification: INotification): void
  }>()

  // Refs and reactive variables
  const { t } = useI18n()
  const resolveActions = injectNotificationItemActions()

  // Computed properties
  const isNotificationLoading = computed(() => props.isLoading)
  const rows = computed(() =>
    props.notifications.map(notification => ({
      notification,
      actions: resolveActions(notification),
    }))
  )
  // User-defined functions
  function handleItemClick(notification: INotification) {
    emit('itemClicked', notification)
  }
</script>

<template>
  <div>
    <div v-if="props.notifications.length > 0" :id="createElementId('notification', 'list', 'container')" class="pb-2">
      <NotificationItem v-for="row in rows" :key="row.notification._id" :notification="row.notification" @onItemClicked="handleItemClick">
        <template v-if="row.actions" #actions>
          <component :is="row.actions" :notification="row.notification" :notifications="props.notifications" />
        </template>
      </NotificationItem>
    </div>

    <div v-else-if="!isNotificationLoading">
      <div class="justify-top flex h-full flex-col items-center px-8 py-12 text-center">
        <div class="rounded-[28px] bg-primary-50 p-2.5">
          <div class="rounded-[28px] bg-surface px-2.5 pb-2 pt-2.5 shadow-sm">
            <BellRinging04Icon class="h-6 w-6 text-primary" />
          </div>
        </div>
        <CPText size="lg" weight="medium" class="mt-4 text-body">
          {{ t('notifications.noNotifications') }}
        </CPText>
        <CPText size="sm" class="mt-1 max-w-[16rem] text-muted">
          {{ t('notifications.whenYouGetNotifications') }}
        </CPText>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div
      v-if="isNotificationLoading || props.isFetchingNextPage"
      :id="createElementId('notification', 'list', 'loading-skeleton')"
      class="flex flex-col items-center justify-center"
      :class="{ 'py-2': props.notifications.length > 0 }"
    >
      <div v-for="i in 2" :key="`loading-${i}`" class="w-full">
        <div class="py-4 px-2 gap-2 flex flex-col box-border">
          <!-- First row: Avatar and notification body -->
          <div class="flex w-full gap-2 items-center">
            <div class="relative flex-shrink-0">
              <CPSkeleton :rounded="true" width="40px" height="40px" />
            </div>
            <div class="flex min-w-0 flex-col justify-between w-full gap-1">
              <div class="flex-1 min-w-0">
                <CPSkeleton type="text" :repeat="1" height="14px" class="leading-relaxed" />
                <CPSkeleton type="text" :repeat="1" height="14px" width="60%" class="leading-relaxed" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  @media (max-width: 1024px) {
    .notification-height {
      max-height: calc(100vh - 60px);
    }
  }

  @media (min-width: 769px) {
    .notification-height {
      max-height: calc(100vh - 87px);
    }
  }
</style>
